import type { DeviceIdentity } from "./link";

/**
 * The one plain-text command in the serial protocol, and its answer.
 *
 * §4 of the kit spec: `SPONTAR IDENTITY\n` is answered with one line,
 * `deviceId=<id> publicKey=<b64url> fw=<semver>\n`. Everything else the
 * device prints on the same port — boot logs, Improv frames — is noise here
 * and is skipped until that line arrives.
 */
export const IDENTITY_COMMAND = "SPONTAR IDENTITY\n";

const DEVICE_ID = /^cue-[a-z2-7]{10}$/;
const PUBLIC_KEY = /^[A-Za-z0-9_-]{43}$/;
const VERSION = /^\d+\.\d+\.\d+(?:[-+][0-9A-Za-z.-]+)?$/;

/** Reads one line as an identity, or nothing if it is not one. */
export function parseIdentityLine(line: string): DeviceIdentity | undefined {
  const fields = new Map<string, string>();
  for (const token of line.trim().split(/\s+/)) {
    const at = token.indexOf("=");
    if (at <= 0) return undefined;
    fields.set(token.slice(0, at), token.slice(at + 1));
  }

  const deviceId = fields.get("deviceId");
  const publicKey = fields.get("publicKey");
  const fw = fields.get("fw");
  if (!deviceId || !publicKey || !fw) return undefined;
  if (!DEVICE_ID.test(deviceId) || !PUBLIC_KEY.test(publicKey) || !VERSION.test(fw)) {
    return undefined;
  }

  /*
    `board` is new and optional, and an older firmware that does not send it is
    not saying its board is fine — it is saying nothing, which is why this is
    left absent rather than defaulted.
  */
  const board = fields.get("board");
  return { deviceId, publicKey, fw, ...(board ? { board } : {}) };
}

/**
 * Finds the identity line in whatever has come off the wire so far.
 *
 * Fed the growing buffer rather than a line at a time, because serial reads
 * split wherever they like — the line can arrive in three pieces, or in the
 * middle of a boot log. Only complete lines are considered.
 */
export function findIdentity(buffer: string): DeviceIdentity | undefined {
  const lines = buffer.split(/\r?\n/);
  // The last piece has no newline yet; it is not a line.
  lines.pop();
  for (const line of lines) {
    const identity = parseIdentityLine(line);
    if (identity) return identity;
  }
  return undefined;
}
